// The presence poll, in its own module.
//
// Deliberately NOT inside lib/presence.ts: that module is the data seam every
// test mocks, and a hook calling listOnTheClock() from inside the same module
// would bypass the mock — the call never crosses a module boundary, so vi.mock
// cannot intercept it. Keeping the fetch and the hook apart is what lets both
// the card and the roster be tested through one stub.
import { useEffect, useMemo, useState } from 'react';
import { listOnTheClock } from './presence';

const REFRESH_MS = 60_000;

/**
 * Who is on the clock, polled. Shared by the On-the-clock card and the People
 * roster pip so there is one poll shape rather than two that can drift apart.
 *
 * `rows === null` means "not loaded yet" — never "nobody". A surface that
 * conflates the two renders a confident empty state before the first fetch lands.
 */
export function useOnTheClock() {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof listOnTheClock>> | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let live = true;
    const load = () =>
      void listOnTheClock().then((r) => {
        if (!live) return;
        setRows(r);
        setNow(Date.now());
      });
    load();
    // Polled rather than realtime: presence changes a handful of times a day,
    // and a realtime subscription on time_entries would deliver only the
    // viewer's OWN rows anyway, because realtime honours RLS.
    const t = setInterval(load, REFRESH_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  // Keyed for a row lookup. The RPC keys on profile_id and every roster keys on
  // profiles.id — the same uuid under two names, which is the one thing here
  // that can silently break.
  const byProfile = useMemo(() => new Map((rows ?? []).map((r) => [r.profile_id, r])), [rows]);

  return { rows, now, byProfile };
}
